import React, { useEffect, useState } from "react";
import { Icon, initialsOf } from "./icons";
import "./ManageContacts.css";

/* ✅ CLOUD BACKEND */
const API = "https://backend-9i6n.onrender.com";

const AVATAR_COLORS = [
  "linear-gradient(135deg,#1B6CA8,#12507e)",
  "linear-gradient(135deg,#2E7D32,#1f5a24)",
  "linear-gradient(135deg,#5c6bc0,#3949ab)",
  "linear-gradient(135deg,#e07b39,#c85f1e)",
];

const roles = [
  "Sarpanch",
  "Upa-Sarpanch",
  "Ward Member",
  "Gram Sevak",
  "VDO",
  "Rozgar Sevak",
  "Anganwadi Worker",
  "ASHA Worker",
];

const ManageContacts = () => {
  const token = localStorage.getItem("token");

  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ===== FORM STATES ===== */
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  /* ===== TOAST ===== */
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success", // success | error
  });

  /* ===== TOAST HANDLER ===== */
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 2500);
  };

  /* ===== FETCH MEMBERS ===== */
  const fetchMembers = async () => {
    try {
      const res = await fetch(`${API}/api/contacts`);
      const data = await res.json();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast("सदस्य माहिती मिळवता आली नाही", "error");
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  /* ===== PHOTO PREVIEW ===== */
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setPhoto(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  /* ===== ADD MEMBER ===== */
  const addMember = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("role", role);
    formData.append("phone", phone);
    formData.append("email", email);
    if (photo) formData.append("photo", photo);

    try {
      await fetch(`${API}/api/contacts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      setName("");
      setRole("");
      setPhone("");
      setEmail("");
      setPhoto(null);
      setPhotoPreview(null);

      fetchMembers();
      showToast("सदस्य यशस्वीरित्या जोडला", "success");
    } catch (err) {
      showToast("सदस्य जोडता आला नाही", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ===== DELETE MEMBER ===== */
  const deleteMember = async (id) => {
    if (!window.confirm("हा सदस्य नक्की हटवायचा आहे का?")) return;

    try {
      await fetch(`${API}/api/contacts/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      setSelectedMember(null);
      fetchMembers();
      showToast("सदस्य यशस्वीरित्या हटवला", "success");
    } catch {
      showToast("हटवणे अयशस्वी झाले", "error");
    }
  };

  /* ===== UPDATE MEMBER ===== */
  const updateMember = async () => {
    try {
      await fetch(`${API}/api/contacts/${selectedMember._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: selectedMember.name,
          role: selectedMember.role,
          phone: selectedMember.phone,
          email: selectedMember.email,
        }),
      });

      setEditMode(false);
      fetchMembers();
      showToast("सदस्य यशस्वीरित्या अद्ययावत केला", "success");
    } catch {
      showToast("अद्ययावत करता आले नाही", "error");
    }
  };

  if (!token) {
    return (
      <div className="admin-page">
        <div className="gp-empty">कृपया प्रशासक म्हणून लॉगिन करा</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="gp-page-head">
        <div>
          <h1>ग्रामपंचायत सदस्य</h1>
          <p>निर्वाचित सदस्य व प्रशासकीय कर्मचारी व्यवस्थापित करा</p>
        </div>
      </div>

      {/* ===== ADD FORM ===== */}
      <div className="gp-card member-form-card">
        <h3 className="member-form-title">नवीन सदस्य जोडा</h3>
        <form className="member-form-grid" onSubmit={addMember}>
          <input
            className="gp-input"
            placeholder="पूर्ण नाव"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <select
            className="gp-select"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            required
          >
            <option value="">पद निवडा</option>
            {roles.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
          <input
            className="gp-input"
            placeholder="फोन नंबर"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
          <input
            className="gp-input"
            placeholder="ईमेल"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input className="gp-input" type="file" accept="image/*" onChange={handlePhotoChange} />

          {photoPreview && <img src={photoPreview} className="member-preview-img" alt="preview" />}

          <button type="submit" className="gp-btn gp-btn-green" disabled={loading}>
            {loading ? (
              <span className="gp-spinner" />
            ) : (
              <>
                <Icon.plus size={16} /> सदस्य जोडा
              </>
            )}
          </button>
        </form>
      </div>

      {/* ===== MEMBER LIST ===== */}
      {members.length === 0 ? (
        <div className="gp-empty" style={{ marginTop: 22 }}>
          अद्याप कोणतेही सदस्य जोडलेले नाहीत
        </div>
      ) : (
        <div className="member-grid" style={{ marginTop: 22 }}>
          {members.map((m, i) => (
            <div className="gp-card gp-card--hover member-card" key={m._id}>
              {m.photo ? (
                <img src={m.photo} alt={m.name} className="member-photo" />
              ) : (
                <div
                  className="gp-avatar gp-avatar-round member-initials"
                  style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                >
                  {initialsOf(m.name)}
                </div>
              )}
              <h4>{m.name}</h4>
              <span className="gp-badge gp-badge-blue">{m.role}</span>
              <button className="gp-btn gp-btn-ghost gp-btn-sm member-view-btn" onClick={() => setSelectedMember(m)}>
                पहा
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ===== MODAL ===== */}
      {selectedMember && (
        <div className="gp-modal-overlay" onClick={() => { setSelectedMember(null); setEditMode(false); }}>
          <div className="gp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gp-modal-head">
              <h3>सदस्य तपशील</h3>
              <button
                className="gp-modal-close"
                onClick={() => { setSelectedMember(null); setEditMode(false); }}
              >
                <Icon.x size={15} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label className="gp-field">
                नाव
                <input
                  className="gp-input"
                  value={selectedMember.name}
                  disabled={!editMode}
                  onChange={(e) => setSelectedMember({ ...selectedMember, name: e.target.value })}
                />
              </label>
              <label className="gp-field">
                पद
                <input
                  className="gp-input"
                  value={selectedMember.role}
                  disabled={!editMode}
                  onChange={(e) => setSelectedMember({ ...selectedMember, role: e.target.value })}
                />
              </label>
              <label className="gp-field">
                फोन
                <input
                  className="gp-input"
                  value={selectedMember.phone}
                  disabled={!editMode}
                  onChange={(e) => setSelectedMember({ ...selectedMember, phone: e.target.value })}
                />
              </label>
            </div>

            <div className="gp-modal-actions">
              {!editMode ? (
                <button className="gp-btn gp-btn-blue" onClick={() => setEditMode(true)}>
                  <Icon.edit size={15} /> संपादित करा
                </button>
              ) : (
                <button className="gp-btn gp-btn-green" onClick={updateMember}>
                  जतन करा
                </button>
              )}
              <button
                className="gp-btn gp-btn-danger-outline"
                onClick={() => deleteMember(selectedMember._id)}
              >
                <Icon.trash size={15} /> हटवा
              </button>
              <button
                className="gp-btn gp-btn-ghost"
                onClick={() => { setSelectedMember(null); setEditMode(false); }}
              >
                बंद करा
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== TOAST ===== */}
      {toast.show && (
        <div className={`gp-toast gp-toast-${toast.type === "error" ? "error" : "success"}`}>
          <p style={{ margin: 0 }}>{toast.message}</p>
        </div>
      )}
    </div>
  );
};

export default ManageContacts;
